import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import { yupResolver } from '@hookform/resolvers/yup';

import { AppContext } from 'context';
import { useSortingCenterById } from 'hooks';
import Loading from 'components/Loading';
import { notify } from 'components/Notifier';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { CardLine, StyledCard } from 'components/List/StyledCard';
import { ALPHANUMERIC_REGEX, NUMERIC_REGEX } from 'utils/formatter';
import { Contact, ContactPost, Event, SortingCenter } from 'types';
import client from 'services/client';
import { sortingCenterInfoSchema } from './SortingCenterInfo.yup';

function formatContact(contact: Contact): ContactPost;
function formatContact(contact: null): null;
function formatContact(contact: Contact | null): ContactPost | null {
  if (!contact) return null;
  return {
    firstName: contact.firstName ?? '',
    lastName: contact.lastName ?? '',
    email: contact.email ?? '',
    phoneNumber: contact.phoneNumber ?? '',
  };
}

export const SortingCenterInfo: React.FC<{}> = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'sortingCenters.info' });
  const context = useContext(AppContext);

  const sortingCenter = context.getSortingCenter?.();
  const sortingCenterId = transformObjectId(sortingCenter);
  const { value: sortingCenterFromHook, loading } = useSortingCenterById(sortingCenterId ?? null);

  const [sortingCenterDetails, setSortingCenterDetails] = useState<CardLine[]>([]);
  const [sortingCenterContact, setSortingCenterContact] = useState<CardLine[]>([]);

  const roles = context.getRoles?.();
  const isEditable = sortingCenter?.isActive && roles && context.hasLeastOneAdminRole();

  useEffect(() => {
    if (loading || !sortingCenterFromHook) {
      setSortingCenterDetails([]);
      setSortingCenterContact([]);
      return;
    }
    setSortingCenterDetails([
      { key: 'code', label: t('code'), value: sortingCenterFromHook.code },
      {
        key: 'name',
        label: t('name'),
        value: sortingCenterFromHook.name,
        editable: true,
        validationRegex: ALPHANUMERIC_REGEX,
        maxLength: 255,
      },
      {
        key: 'addressLine',
        label: t('addressLine'),
        value: sortingCenterFromHook.addressLine,
        editable: true,
        validationRegex: ALPHANUMERIC_REGEX,
        maxLength: 255,
      },
      {
        key: 'postalCode',
        label: t('postalCode'),
        value: sortingCenterFromHook.postalCode,
        editable: true,
        validationRegex: ALPHANUMERIC_REGEX,
        maxLength: 255,
      },
      {
        key: 'city',
        label: t('city'),
        value: sortingCenterFromHook.city,
        editable: true,
        validationRegex: ALPHANUMERIC_REGEX,
        maxLength: 255,
      },
      {
        key: 'phoneNumber',
        label: t('phoneNumber'),
        value: sortingCenterFromHook.phoneNumber,
        editable: true,
        validationRegex: NUMERIC_REGEX,
        maxLength: 12,
      },
    ]);
    setSortingCenterContact([
      {
        key: 'lastName',
        label: t('lastName'),
        value: sortingCenterFromHook.contact?.lastName,
        editable: true,
        validationRegex: ALPHANUMERIC_REGEX,
        maxLength: 255,
      },
      {
        key: 'firstName',
        label: t('firstName'),
        value: sortingCenterFromHook.contact?.firstName,
        editable: true,
        validationRegex: ALPHANUMERIC_REGEX,
        maxLength: 255,
      },
      {
        key: 'phoneNumber',
        label: t('phoneNumber'),
        value: sortingCenterFromHook.contact?.phoneNumber,
        editable: true,
        validationRegex: NUMERIC_REGEX,
        maxLength: 12,
      },
    ]);
  }, [loading, sortingCenterFromHook, setSortingCenterDetails, setSortingCenterContact, t]);

  const updateSortingCenter = useCallback(
    (sortingCenter: SortingCenter) => {
      if (!sortingCenterId) return;
      try {
        client.put(`/sorting_centers/${sortingCenterId}`, {
          ...sortingCenter,
          contact: formatContact(sortingCenter.contact),
        });
        PubSub.publish(Event.REFRESH_SORTING_CENTER);
        notify({
          message: t('updated'),
          severity: 'success',
        });
      } catch {
        notify({
          message: t('errorGeneric'),
          severity: 'error',
        });
      }
    },
    [sortingCenterId, t],
  );

  const submitSortingCenterDetails = useCallback(
    (e: Partial<SortingCenter>) => {
      if (!sortingCenterFromHook) return;
      updateSortingCenter({ ...sortingCenterFromHook, ...e });
    },
    [sortingCenterFromHook, updateSortingCenter],
  );

  const submitSortingCenterContact = useCallback(
    (e: Partial<Contact>) => {
      if (!sortingCenterFromHook) return;
      updateSortingCenter({ ...sortingCenterFromHook, contact: { ...sortingCenterFromHook.contact, ...e } });
    },
    [sortingCenterFromHook, updateSortingCenter],
  );

  const schema = useMemo(() => sortingCenterInfoSchema(t), [t]);
  const resolver = yupResolver(schema);

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <Grid container spacing={9}>
          <Grid item xs={6}>
            <StyledCard
              title={t('details')}
              lines={sortingCenterDetails}
              editable={isEditable}
              onSubmit={submitSortingCenterDetails}
              resolver={resolver}
            />
          </Grid>
          <Grid item xs={6}>
            <StyledCard
              title={t('contact')}
              lines={sortingCenterContact}
              editable={isEditable}
              onSubmit={submitSortingCenterContact}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};
